import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Popover } from 'components/atoms/Popover';
import { DropdownCaret } from 'components/atoms/DropdownCaret';
import {
  FilterListbox,
  FilterListboxOption,
} from 'components/molecules/FilterListbox';
import { currencyFormat } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import {
  CalendarAgendaItem,
  CalendarAvailabilityState,
  CalendarSession,
} from 'api/calendar/types';
import { RowDisplay } from './scheduleView.types';
import { formatDurations, formatTime } from './scheduleViewUtils';

interface SessionOptionProps {
  readonly session: CalendarSession;
  readonly label: string;
  readonly disabled: boolean;
  readonly onSelect: (session: CalendarSession) => void;
}

const SessionOption = ({
  session,
  label,
  disabled,
  onSelect,
}: SessionOptionProps) => {
  const handleSelect = () => onSelect(session);
  return (
    <FilterListboxOption
      ariaLabel={label}
      className="schedule-view__session-option"
      disabled={disabled}
      onSelect={handleSelect}
      selected={false}
    >
      {label}
    </FilterListboxOption>
  );
};

const DEFAULT_ROW_DISPLAY: RowDisplay = {
  image: true,
  duration: true,
  spotsRemaining: true,
};

interface AgendaRowProps {
  readonly item: CalendarAgendaItem;
  readonly sessions: ReadonlyArray<CalendarSession>;
  /**
   * When true the row's sessions are collapsed into a single per-row dropdown (the row has more
   * than the collapse threshold of sessions). When false the row represents a single session and
   * renders inline.
   */
  readonly collapsed: boolean;
  readonly locale: string;
  /** Mobile viewport: the explicit CTA is hidden and the whole row taps to book. */
  readonly isMobile?: boolean;
  readonly onBook: (item: CalendarAgendaItem, session: CalendarSession) => void;
  readonly rowDisplay?: RowDisplay;
}

const isPastState = (state: CalendarAvailabilityState) =>
  state === 'PAST' || state === 'PAST_CUTOFF';

const aggregateState = (
  sessions: ReadonlyArray<CalendarSession>,
): CalendarAvailabilityState => {
  const has = (state: CalendarAvailabilityState) =>
    sessions.some((session) => session.state === state);
  if (has('AVAILABLE') || has('LOW')) return 'AVAILABLE';
  if (has('SOLD_OUT_WAITLIST')) return 'SOLD_OUT_WAITLIST';
  if (has('SOLD_OUT')) return 'SOLD_OUT';
  if (has('PAST_CUTOFF')) return 'PAST_CUTOFF';
  return 'PAST';
};

const thumbnailUrl = (item: CalendarAgendaItem): string | null => {
  const uri = item.thumbnail?.uri;
  if (!uri) return null;
  const candidate = uri['384w'] || uri['480w'] || uri.original;
  return candidate && candidate.startsWith('http') ? candidate : null;
};

const AgendaRow = ({
  item,
  sessions,
  collapsed,
  locale,
  isMobile = false,
  onBook,
  rowDisplay = DEFAULT_ROW_DISPLAY,
}: AgendaRowProps) => {
  const { t: translate } = useTranslation();
  const brandCurrency = useBrandCurrency();
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  // On multi-session rows — a collapsed dropdown, or an inline slot that merges several durations
  // sharing one start time — the summary time and default-book target rotate forward to the
  // earliest still-bookable session, and the CTA reflects the aggregate state (bookable if ANY
  // session is). A not-bookable session that happens to be earliest shouldn't represent the row,
  // but still appears in the dropdown for reference. Single-session rows keep their own state
  // verbatim (so LOW-inventory urgency still shows). SOLD_OUT_WAITLIST counts as bookable here.
  const isSessionBookable = (session: CalendarSession): boolean =>
    session.state !== 'PAST' &&
    session.state !== 'PAST_CUTOFF' &&
    session.state !== 'SOLD_OUT';
  const multiSession = sessions.length > 1;
  const lead =
    (multiSession && sessions.find(isSessionBookable)) || sessions[0];
  const state = multiSession ? aggregateState(sessions) : lead.state;
  const handleBookLead = () => onBook(item, lead);
  // Stop the row-level tap handler from double-firing onBook on desktop.
  const handleBookButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    handleBookLead();
  };

  // Single-session bookable rows navigate to the experience view (deep-linked to this session's
  // date+time via handleBook in ScheduleViewPage). Collapsed rows book via their per-row session
  // dropdown, so they're never row-clickable. On mobile the explicit CTA is hidden, so the row
  // becomes the accessible tap target (role="button" + keyboard). On desktop the CTA is still
  // visible; keeping role/tabIndex off the row avoids a nested-interactive control alongside
  // the button (ADR-007) — the row's onClick there is a mouse-only ergonomic enhancement, and
  // keyboard/screen-reader users continue booking via the Book button.
  const notBookableStates = ['PAST', 'PAST_CUTOFF', 'SOLD_OUT'];
  const ctaBookable = !notBookableStates.includes(state);
  const rowClickable = !collapsed && ctaBookable;
  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBookLead();
    }
  };
  const rowInteractionProps = (() => {
    if (!rowClickable) return {};
    if (isMobile) {
      return {
        role: 'button',
        tabIndex: 0,
        'aria-label': translate('bookExperienceAriaLabel', {
          defaultValue: 'Book {{title}}',
          title: item.title,
        }),
        onClick: handleBookLead,
        onKeyDown: handleRowKeyDown,
      };
    }
    return { onClick: handleBookLead };
  })();

  // Starting "from $X" / "Complimentary" price for the item (shared by inline rows and dropdown).
  // Formatted with the brand's currency object like the rest of the embed — the calendar feed's
  // item.currency is a bare code the API doesn't populate yet, and interpolating the raw number
  // rendered fractional prices with a single decimal (e.g. "$69.6" instead of "$69.60").
  const formatPrice = (): string => {
    if (item.startingPrice == null) return '';
    if (item.startingPrice === 0)
      return translate('complimentary', { defaultValue: 'Complimentary' });
    const amount = currencyFormat(brandCurrency)(item.startingPrice);
    return `${translate('from', { defaultValue: 'from' })} ${amount}`;
  };
  const priceLabel = formatPrice();

  const durationLabels = {
    hour: translate('durationHour', { defaultValue: 'hour' }),
    hours: translate('durationHours', { defaultValue: 'hours' }),
    minutes: translate('durationMinutes', { defaultValue: 'min' }),
  };
  // Show every duration offered at this start time (a 30-min and a 1-hour slot both at 6:00am read
  // "30 min, 1 hour"), sorted ascending and capped at three with a "+N more" tail. Reads the row's
  // own sessions rather than item.durationMinutes — the item carries a single duration, which is
  // what made every row for a listing show the same value.
  const durationText = rowDisplay.duration
    ? formatDurations(
        sessions.map((session) => session.durationMinutes),
        durationLabels,
        {
          maxShown: 3,
          more: (count) => `+${count} ${translate('more').toLowerCase()}`,
        },
      )
    : '';
  const metaParts = [durationText || null, priceLabel || null].filter(Boolean);

  // "N left" urgency only on single-session (inline) rows.
  const lowSpots =
    rowDisplay.spotsRemaining &&
    !collapsed &&
    lead.state === 'LOW' &&
    lead.spotsRemaining != null
      ? lead.spotsRemaining
      : null;
  const showUrgency = lowSpots != null;

  const bookLabel =
    item.ctaLabel ||
    translate('bookExperience', { defaultValue: 'Book experience' });

  // ---- per-row session dropdown (collapsed rows) -------------------------------------------
  // A single accessible <select> listing every session: time + seats-left + price. Selecting a
  // non-past session books it. A leading placeholder keeps onChange firing for every real pick.
  const seatsLabel = (session: CalendarSession): string => {
    if (
      !rowDisplay.spotsRemaining ||
      session.spotsRemaining == null ||
      isPastState(session.state)
    )
      return '';
    return translate('spotsLeft', {
      defaultValue: '{{count}} left',
      count: session.spotsRemaining,
    });
  };

  const sessionOptionLabel = (session: CalendarSession): string => {
    const parts = [formatTime(session.startDateTime, locale)];
    const seats = seatsLabel(session);
    if (seats) parts.push(seats);
    if (priceLabel) parts.push(priceLabel);
    return parts.join(' • ');
  };

  const handleSessionSelect = (session: CalendarSession) => {
    if (isPastState(session.state)) return;
    setSessionMenuOpen(false);
    onBook(item, session);
  };

  const chooseTimeLabel = translate('chooseATime', {
    defaultValue: 'Choose a time for {{title}}',
    title: item.title,
  });
  const placeholderLabel = translate('sessionsCount', {
    defaultValue: '{{count}} times — choose a time',
    count: sessions.length,
  });

  const renderSessionDropdown = () => (
    <Popover
      asChild
      portalled
      open={sessionMenuOpen}
      onOpenChange={setSessionMenuOpen}
      align="start"
      side="bottom"
      sideOffset={6}
      aria-label={chooseTimeLabel}
      trigger={
        <SessionTrigger
          type="button"
          className="schedule-view__session-select"
          aria-haspopup="listbox"
          aria-expanded={sessionMenuOpen}
          aria-label={chooseTimeLabel}
        >
          <TimeSelectorSpan className="schedule-view__session-label">
            {placeholderLabel}
          </TimeSelectorSpan>
          <DropdownCaret
            className="schedule-view__session-caret"
            open={sessionMenuOpen}
          />
        </SessionTrigger>
      }
    >
      <FilterListbox ariaLabel={chooseTimeLabel}>
        {sessions.map((session) => (
          <SessionOption
            key={`${session.startDateTime}-${session.durationMinutes}`}
            session={session}
            label={sessionOptionLabel(session)}
            disabled={isPastState(session.state)}
            onSelect={handleSessionSelect}
          />
        ))}
      </FilterListbox>
    </Popover>
  );

  const renderCta = () => {
    if (state === 'PAST') return null;
    if (state === 'PAST_CUTOFF') {
      return (
        <span className="schedule-view__cta-muted">
          {translate('pastBookingCutoff', {
            defaultValue: 'Past booking cutoff',
          })}
        </span>
      );
    }
    if (state === 'SOLD_OUT') {
      return (
        <span className="schedule-view__cta-soldout">
          {translate('soldOut', { defaultValue: 'Sold out' })}
        </span>
      );
    }
    if (state === 'SOLD_OUT_WAITLIST') {
      return (
        <button
          type="button"
          className="schedule-view__book schedule-view__waitlist-button"
          aria-label={translate('joinWaitlistAriaLabel', {
            defaultValue: 'Join waitlist for {{title}}',
            title: item.title,
          })}
          onClick={handleBookButtonClick}
        >
          {translate('joinWaitlist', { defaultValue: 'Join waitlist' })}
        </button>
      );
    }
    return (
      <button
        type="button"
        className="schedule-view__book schedule-view__book-button"
        aria-label={translate('bookExperienceAriaLabel', {
          defaultValue: 'Book {{title}}',
          title: item.title,
        })}
        onClick={handleBookButtonClick}
      >
        {bookLabel}
      </button>
    );
  };

  const imageUrl = rowDisplay.image ? thumbnailUrl(item) : null;

  return (
    <Row
      data-tappable={rowClickable}
      className="schedule-view__row"
      {...rowInteractionProps}
    >
      <RowLeft className="schedule-view__row-left">
        {/* The time gutter shows the session time (single-session rows) or the FIRST session's
            time (collapsed rows) so the column is never empty and thumbnails stay aligned. */}
        <RowTime className="schedule-view__row-time">
          {formatTime(lead.startDateTime, locale)}
        </RowTime>
        {rowDisplay.image && (
          <RowThumb className="schedule-view__row-thumb">
            {imageUrl && <img src={imageUrl} alt="" />}
          </RowThumb>
        )}
        <RowBody className="schedule-view__row-body">
          <RowTitle className="schedule-view__row-title">{item.title}</RowTitle>
          {metaParts.length > 0 && (
            <RowMeta className="schedule-view__row-meta">
              {metaParts.map((part, index) => {
                const isPrice = part === priceLabel;
                return (
                  <React.Fragment key={part}>
                    {index > 0 && (
                      <span
                        aria-hidden="true"
                        className="schedule-view__row-meta-sep"
                      >
                        •
                      </span>
                    )}
                    <span
                      className={
                        isPrice
                          ? 'schedule-view__row-meta-item schedule-view__row-price'
                          : 'schedule-view__row-meta-item'
                      }
                    >
                      {part}
                    </span>
                  </React.Fragment>
                );
              })}
            </RowMeta>
          )}
          {collapsed && renderSessionDropdown()}
        </RowBody>
      </RowLeft>
      <RowRight className="schedule-view__row-cta">
        {showUrgency && (
          <SpotsLeft
            className="schedule-view__spots-left"
            aria-label={translate('spotsLeftAriaLabel', {
              defaultValue: '{{count}} spots remaining',
              count: lowSpots,
            })}
          >
            {translate('spotsLeft', {
              defaultValue: '{{count}} left',
              count: lowSpots,
            })}
          </SpotsLeft>
        )}
        {/* Collapsed rows keep the CTA alongside the dropdown so users can one-tap-book the
            (rotated-forward) lead session without opening the popover. `renderCta` reads the
            aggregate `state`, so it stays consistent — PAST if every session is past, AVAILABLE
            once any session in the group is still bookable, etc. */}
        {renderCta()}
      </RowRight>
    </Row>
  );
};

// Memoised so a re-render of the parent (day strip selection, scroll-spy URL rewrite, etc.) only
// reconciles rows whose inputs actually changed. Relies on stable identities for `onBook` and
// `rowDisplay` from the ScheduleViewPage owner — see the latest-ref pattern there.
export default React.memo(AgendaRow);

const rowIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  width: 100%;

  /* Single-session bookable rows tap to book (the booking control on mobile). */
  &[data-tappable='true'] {
    cursor: pointer;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${rowIn} 240ms ease both;
  }

  @media all and (min-width: 768px) {
    align-items: center;
    gap: 16px;
  }

  /* Mobile hides the explicit CTA — the whole row is the tap target (per design). */
  @media all and (max-width: 767px) {
    .schedule-view__book {
      display: none;
    }
  }
`;

const RowLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;

  @media all and (min-width: 768px) {
    align-items: center;
    gap: 32px;
  }
`;

const RowTime = styled.div`
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.2px;
  color: var(--way-colors-primaryTextColor);
  width: 56px;
  flex: 0 0 auto;

  @media all and (min-width: 768px) {
    font-size: 15px;
    width: 84px;
  }
`;

const RowThumb = styled.div`
  width: 56px;
  height: 42px;
  flex: 0 0 auto;

  @media all and (min-width: 768px) {
    width: 96px;
    height: 72px;
  }
  overflow: hidden;
  background: linear-gradient(
    150deg,
    var(--way-colors-contrastColorShades-5) 13%,
    var(--way-colors-contrastColorShades-40) 78%
  );

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const RowBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  @media all and (max-width: 769px) {
    gap: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RowTitle = styled.div`
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: -0.16px;
  color: var(--way-colors-primaryTextColor);

  @media all and (min-width: 768px) {
    font-size: 16px;
  }
`;

/* duration · from $X — all utility/sans, self-healing middots. */
const RowMeta = styled.div`
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  line-height: 1.4;
  color: var(--way-colors-tertiaryTextColor);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  .schedule-view__row-meta-sep {
    color: var(--way-colors-primaryTextColor);
  }
`;

const SpotsLeft = styled.span`
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  letter-spacing: 0.2px;
  color: var(--way-colors-primaryTextColor);
  white-space: nowrap;
`;

const SessionTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  line-height: 1.2;
  color: var(--way-colors-primaryTextColor);
  white-space: nowrap;
  padding: 0;
  margin-top: 4px;
`;

/* Right cluster: starting price, urgency, then the booking CTA (prototype puts price by the CTA). */
const RowRight = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  white-space: nowrap;
  text-align: right;

  @media all and (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 16px;
    font-size: 14px;
  }

  button.schedule-view__book {
    font: inherit;
    background: none;
    border: none;
    padding: 4px 0;
    cursor: pointer;
    color: var(--way-colors-primaryTextColor);
    border-bottom: 0.5px solid var(--way-colors-primaryTextColor);
    transition: opacity 120ms ease;
  }
  button.schedule-view__book:hover {
    opacity: 0.7;
  }
  .schedule-view__cta-muted {
    color: var(--way-colors-tertiaryTextColor);
  }
  /* Sold out reads in the brand's error color (falls back to muted if undefined). */
  .schedule-view__cta-soldout {
    color: var(--way-colors-errorColor, var(--way-colors-tertiaryTextColor));
  }
`;

const TimeSelectorSpan = styled.span`
  color: var(--way-colors-tertiaryTextColor);
  font: inherit;
`;
